import React, { useState, useEffect } from "react";
import { Box, Typography, Alert, Breadcrumbs, useTheme, IconButton } from "@mui/material";

import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import BadgeIcon         from "@mui/icons-material/Badge";
import ChevronRightIcon  from "@mui/icons-material/ChevronRight";
import PersonIcon        from "@mui/icons-material/Person";
import PhoneIcon         from "@mui/icons-material/Phone";
import HomeIcon          from "@mui/icons-material/Home";
import FingerprintIcon   from "@mui/icons-material/Fingerprint";
import GroupsIcon        from "@mui/icons-material/Groups";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CameraAltIcon     from "@mui/icons-material/CameraAlt";

import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";

import FixedSizeDialog             from "components/fixedSizeDialog";
import EmployeeDetailEditForm      from "./employeeDetailEdit";
import EmployeePictureEditForm     from "./employeePictureEdit";
import EmployeeDocumentTable       from "../../tables/employeeDocumentsTable";
import EmployeePayrollTable        from "../../tables/employeePayrollDetailTable";
import EmployeePayslipHistoryTable from "../../tables/employeePayslipHistoryTable";
import { useGetEmployee }          from "api/hooks/humanResource";
import { URL }                     from "api/url";

/* ── Detail field ── */
const DetailField = ({ icon, label, value, textPri, textSec, accent }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px", py: "0.5rem", borderBottom: "0.5px solid rgba(29,158,117,0.07)" }}>
    <Box sx={{ color: accent, flexShrink: 0, mt: "2px", opacity: 0.6 }}>
      {React.cloneElement(icon, { sx: { fontSize: "15px" } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "1px" }}>{label}</Typography>
      <Typography sx={{ fontSize: "13px", color: textPri }}>{value || "—"}</Typography>
    </Box>
  </Box>
);

/* ── Inline tab bar ── */
const TabBar = ({ tabs, active, onChange, accent, textSec, border }) => (
  <Box sx={{ display: "flex", gap: "4px", mb: "1.25rem", borderBottom: `1px solid ${border}`, pb: "0" }}>
    {tabs.map((label, i) => (
      <Box
        key={label}
        onClick={() => onChange(i)}
        sx={{
          px: "1rem", py: "0.6rem",
          fontSize: "13px", fontWeight: 500,
          cursor: "pointer",
          color: active === i ? accent : textSec,
          borderBottom: active === i ? `2px solid ${accent}` : "2px solid transparent",
          backgroundColor: active === i ? "rgba(29,158,117,0.06)" : "transparent",
          borderRadius: "6px 6px 0 0",
          transition: "all 0.15s",
          "&:hover": { color: accent, backgroundColor: "rgba(29,158,117,0.04)" },
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

const EmployeeDetailsForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const employeeId = location.state.id;
  const { employee, setNewEmployeeData } = useGetEmployee(employeeId);

  const [isSuccess,      setIsSuccess]      = useState(false);
  const [isFailed,       setIsFailed]       = useState(false);
  const [openPicture,    setOpenPicture]    = useState(false);
  const [openDetailEdit, setOpenDetailEdit] = useState(false);
  const [activeTab,      setActiveTab]      = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => { setIsSuccess(false); setIsFailed(false); }, 3000);
    return () => clearTimeout(timer);
  }, [isSuccess, isFailed]);

  const name              = employee ? `${employee.first_name} ${employee.last_name}` : "";
  const related_department = employee?.related_department?.department_name ?? "";
  const id_number         = employee?.id_number ?? "";
  const address           = employee?.address ?? "";
  const phone_number      = employee?.phone_number ?? "";
  const next_of_kin_name  = employee?.next_of_kin_name ?? "";
  const next_of_kin_phone = employee?.next_of_kin_phone_number ?? "";
  const date_employed     = employee?.date_employed ?? "";
  const employeeImage     = employee?.image ?? "";

  const TABS = ["Personnel & Payroll", "Payslip History", "Documents"];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* Alerts */}
      {isSuccess && <Alert variant="filled" severity="success" sx={{ borderRadius: "8px" }}>Operation was successful!</Alert>}
      {isFailed  && <Alert variant="filled" severity="error"   sx={{ borderRadius: "8px" }}>Operation was not successful!</Alert>}

      {/* Breadcrumb */}
      <Breadcrumbs separator={<ChevronRightIcon sx={{ fontSize: "14px" }} />}>
        <Typography onClick={() => navigate("/humanResource")} sx={{ fontSize: "12px", color: accent, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { textDecoration: "underline" } }}>
          <BadgeIcon sx={{ fontSize: "14px" }} /> Human Resource
        </Typography>
        <Typography sx={{ fontSize: "12px", color: textSec }}>Employee Details</Typography>
      </Breadcrumbs>

      {/* Main layout */}
      <Box sx={{ display: "flex", gap: "1.5rem", alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* ── Left: profile card ── */}
        <Box sx={{ flex: "0 0 240px", display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", overflow: "hidden" }}>
            {/* Photo */}
            <Box sx={{ position: "relative" }}>
              <img
                alt={name}
                src={`${URL}${employeeImage}`}
                style={{ width: "100%", height: "220px", objectFit: "cover", display: "block" }}
              />
              <Box
                onClick={() => setOpenPicture(true)}
                sx={{
                  position: "absolute", bottom: "8px", right: "8px",
                  width: "32px", height: "32px", borderRadius: "8px",
                  backgroundColor: "rgba(0,0,0,0.55)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", backdropFilter: "blur(4px)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                }}
              >
                <CameraAltIcon sx={{ fontSize: "16px", color: "#fff" }} />
              </Box>
            </Box>

            {/* Name + dept */}
            <Box sx={{ p: "0.875rem 1rem" }}>
              <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "16px", fontWeight: 400, color: textPri, lineHeight: 1.3 }}>{name}</Typography>
              <Typography sx={{ fontSize: "12px", color: accent, mt: "2px" }}>{related_department}</Typography>
              {employee?.related_role?.role_name && (
                <Box sx={{ display: "inline-flex", alignItems: "center", px: "7px", py: "2px", borderRadius: "20px", backgroundColor: "rgba(29,158,117,0.1)", mt: "6px" }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 500, color: accent }}>{employee.related_role.role_name}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Right: tabs ── */}
        <Box sx={{ flex: "1 1 500px" }}>
          <TabBar tabs={TABS} active={activeTab} onChange={setActiveTab} accent={accent} textSec={textSec} border={border} />

          {/* Tab 0 — Personnel & Payroll */}
          {activeTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {/* Details card */}
              <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", overflow: "hidden" }}>
                <Box sx={{ px: "1.25rem", py: "0.875rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>Employee Details</Typography>
                  <IconButton onClick={() => setOpenDetailEdit(true)} size="small" sx={{ color: accent, border: `1px solid ${border}`, borderRadius: "7px", "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                    <EditOutlinedIcon sx={{ fontSize: "15px" }} />
                  </IconButton>
                </Box>
                <Box sx={{ p: "0.5rem 1.25rem 1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 2rem" }}>
                  <DetailField icon={<PersonIcon />}        label="Full Name"            value={name}               textPri={textPri} textSec={textSec} accent={accent} />
                  <DetailField icon={<FingerprintIcon />}   label="ID Number"            value={id_number}          textPri={textPri} textSec={textSec} accent={accent} />
                  <DetailField icon={<PhoneIcon />}         label="Phone Number"         value={phone_number}       textPri={textPri} textSec={textSec} accent={accent} />
                  <DetailField icon={<GroupsIcon />}        label="Department"           value={related_department} textPri={textPri} textSec={textSec} accent={accent} />
                  <DetailField icon={<HomeIcon />}          label="Address"              value={address}            textPri={textPri} textSec={textSec} accent={accent} />
                  <DetailField icon={<CalendarTodayIcon />} label="Date of Employment"   value={date_employed ? moment(date_employed).format("LL") : "—"} textPri={textPri} textSec={textSec} accent={accent} />
                  <DetailField icon={<PersonIcon />}        label="Next of Kin"          value={next_of_kin_name}   textPri={textPri} textSec={textSec} accent={accent} />
                  <DetailField icon={<PhoneIcon />}         label="Next of Kin Contact"  value={next_of_kin_phone}  textPri={textPri} textSec={textSec} accent={accent} />
                </Box>
              </Box>

              {/* Payroll card */}
              <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", overflow: "hidden" }}>
                <Box sx={{ px: "1.25rem", py: "0.875rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>Payroll Details</Typography>
                </Box>
                <Box sx={{ p: "1rem 1.25rem" }}>
                  <EmployeePayrollTable employeeId={employeeId} setIsSuccess={setIsSuccess} />
                </Box>
              </Box>
            </Box>
          )}

          {/* Tab 1 — Payslip History */}
          {activeTab === 1 && <EmployeePayslipHistoryTable employeeId={employeeId} />}

          {/* Tab 2 — Documents */}
          {activeTab === 2 && <EmployeeDocumentTable setIsFailed={setIsFailed} setIsSuccess={setIsSuccess} employeeId={employeeId} />}
        </Box>
      </Box>

      {/* ── Dialogs ── */}
      <FixedSizeDialog
        open={openPicture}
        handleClose={() => setOpenPicture(false)}
        dialogHeading="Edit Profile Picture"
        negativeResponse="Cancel"
        maxWidth="md"
        content={
          <Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}>
            <EmployeePictureEditForm setIsSuccess={setIsSuccess} setOpen={setOpenPicture} employeeId={employeeId} setNewEmployeeData={setNewEmployeeData} />
          </Box>
        }
      />

      <FixedSizeDialog
        open={openDetailEdit}
        handleClose={() => setOpenDetailEdit(false)}
        dialogHeading="Edit Employee Details"
        negativeResponse="Cancel"
        maxWidth="md"
        content={
          <Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}>
            <EmployeeDetailEditForm
              data={{
                employeeId, address, phone_number, id_number,
                first_name: employee?.first_name ?? "",
                last_name:  employee?.last_name  ?? "",
                related_department: employee?.related_department?.id ?? "",
                next_of_kin_name,
                next_of_kin_phone_number: next_of_kin_phone,
              }}
              setIsSuccess={setIsSuccess}
              setOpen={setOpenDetailEdit}
              setNewEmployeeData={setNewEmployeeData}
            />
          </Box>
        }
      />
    </Box>
  );
};

export default EmployeeDetailsForm;